<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-lg font-bold text-neutral-900">提示词审核</h2>
          <p class="text-sm text-neutral-500 mt-1">
            审核用户分享的提示词与技能技巧，通过的展示在小程序提示词分享区
          </p>
        </div>
        <button
          @click="refreshList"
          class="px-3 py-2 rounded-lg border border-neutral-300 hover:border-primary hover:text-primary transition-colors"
          title="刷新列表"
        >
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
        </button>
      </div>

      <!-- 筛选 -->
      <div class="flex flex-wrap items-center gap-3 mb-5">
        <div class="flex gap-1 bg-neutral-100 rounded-lg p-1">
          <button
            v-for="s in statusOptions"
            :key="s.value"
            @click="changeStatus(s.value)"
            class="px-3 py-1.5 rounded-md text-sm transition-colors"
            :class="status === s.value ? 'bg-white text-primary shadow-sm font-medium' : 'text-neutral-500 hover:text-neutral-700'"
          >
            {{ s.label }}
          </button>
        </div>
        <div class="relative flex-1 min-w-[200px] max-w-xs">
          <input
            type="text"
            v-model="searchKey"
            @input="handleSearch"
            placeholder="搜索标题/作者..."
            class="w-full px-4 py-2 pl-10 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
          <i class="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"></i>
        </div>
      </div>

      <!-- 列表（两列并排） -->
      <div v-loading="loading" class="grid grid-cols-1 xl:grid-cols-2 gap-4" style="min-height: 120px">
        <div
          v-for="p in list"
          :key="p.id"
          class="bg-neutral-50 rounded-xl border border-neutral-200 p-4 sm:p-5 hover:shadow-md transition-shadow duration-200"
        >
          <div class="flex flex-col sm:flex-row sm:items-start gap-4">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="font-bold text-neutral-900">{{ p.title }}</span>
                <span class="text-xs px-2 py-0.5 rounded-full"
                  :class="p.type === 'skill' ? 'bg-teal-100 text-teal-700' : 'bg-blue-100 text-blue-700'">
                  {{ p.type === 'skill' ? '技能技巧' : '提示词' }}
                </span>
                <span class="text-xs px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-600">
                  {{ p.category }}
                </span>
                <span
                  class="text-xs px-2 py-0.5 rounded-full"
                  :class="{
                    'bg-orange-100 text-orange-700': p.status === 0,
                    'bg-green-100 text-green-700': p.status === 1,
                    'bg-red-100 text-red-700': p.status === 2,
                  }"
                >
                  {{ statusText(p.status) }}
                </span>
              </div>
              <div class="text-sm text-neutral-500 mt-1 flex flex-wrap gap-x-4">
                <span><i class="fa fa-user-o mr-1"></i>{{ p.userName }}</span>
                <span><i class="fa fa-clock-o mr-1"></i>{{ p.createTime }}</span>
                <span><i class="fa fa-eye mr-1"></i>{{ p.viewCount || 0 }}</span>
                <span><i class="fa fa-thumbs-o-up mr-1"></i>{{ p.likeCount || 0 }}</span>
              </div>
              <p class="text-sm text-neutral-600 mt-2 line-clamp-2">{{ p.content }}</p>
              <div v-if="p.status === 2 && p.examInfo" class="mt-2 text-xs text-red-500">
                驳回理由：{{ p.examInfo }}
              </div>
            </div>
            <div class="flex gap-2 shrink-0">
              <button
                @click="viewDetail(p)"
                class="px-3 py-2 rounded-lg border border-neutral-300 hover:border-primary hover:text-primary transition-colors text-sm"
              >
                查看
              </button>
              <template v-if="p.status === 0">
                <button
                  @click="review(p, 1)"
                  class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  <i class="fa fa-check mr-1"></i>通过
                </button>
                <button
                  @click="review(p, 2)"
                  class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
                >
                  <i class="fa fa-times mr-1"></i>驳回
                </button>
              </template>
            </div>
          </div>
        </div>

        <div v-if="!loading && list.length === 0" class="text-center py-12 text-neutral-400 col-span-full">
          <i class="fa fa-file-text-o text-4xl mb-3"></i>
          <p>暂无相关记录</p>
        </div>
      </div>

      <!-- 分页 -->
      <div class="flex items-center justify-between pt-4 col-span-full" v-if="total > 0">
        <span class="text-sm text-neutral-500">共 {{ total }} 条记录</span>
        <div class="flex gap-2">
          <button
            :disabled="page <= 1"
            @click="changePage(page - 1)"
            class="px-3 py-1.5 rounded-lg border border-neutral-300 text-sm disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
          >
            上一页
          </button>
          <span class="px-3 py-1.5 text-sm text-neutral-600">{{ page }} / {{ totalPages }}</span>
          <button
            :disabled="page >= totalPages"
            @click="changePage(page + 1)"
            class="px-3 py-1.5 rounded-lg border border-neutral-300 text-sm disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
          >
            下一页
          </button>
        </div>
      </div>
    </div>

    <!-- 查看全文 -->
    <el-dialog v-model="detailVisible" :title="detail ? detail.title : ''" width="640px">
      <div v-if="detail">
        <div class="text-sm text-neutral-500 mb-4 flex flex-wrap gap-x-4">
          <span><i class="fa fa-user-o mr-1"></i>{{ detail.userName }}</span>
          <span><i class="fa fa-clock-o mr-1"></i>{{ detail.createTime }}</span>
          <span>{{ detail.type === 'skill' ? '技能技巧' : '提示词' }} · {{ detail.category }}</span>
          <span><i class="fa fa-eye mr-1"></i>{{ detail.viewCount || 0 }} 次浏览</span>
        </div>
        <div class="bg-neutral-50 rounded-lg p-4 text-sm text-neutral-800 whitespace-pre-wrap break-all max-h-[50vh] overflow-y-auto">
          {{ detail.content }}
        </div>
        <div v-if="detail.status === 2 && detail.examInfo" class="text-sm text-red-500 mt-3">
          驳回理由：{{ detail.examInfo }}
        </div>
      </div>
      <template #footer>
        <button
          @click="detailVisible = false"
          class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:border-neutral-400 transition-colors text-sm"
        >
          关闭
        </button>
        <button
          v-if="detail && detail.status === 0"
          @click="(detailVisible = false, review(detail, 1))"
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium ml-2"
        >
          通过并关闭
        </button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";

export default {
  name: "PromptManager",
  data() {
    return {
      statusOptions: [
        { label: "待审核", value: 0 },
        { label: "已通过", value: 1 },
        { label: "已驳回", value: 2 },
        { label: "全部", value: -1 },
      ],
      status: 0,
      searchKey: "",
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      loading: false,
      detailVisible: false,
      detail: null,
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
  },
  created() {
    this.getList();
  },
  methods: {
    statusText(s) {
      return { 0: "待审核", 1: "已通过", 2: "已驳回" }[s] || "未知";
    },
    changeStatus(v) {
      this.status = v;
      this.page = 1;
      this.getList();
    },
    refreshList() {
      this.getList();
    },
    handleSearch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.page = 1;
        this.getList();
      }, 400);
    },
    changePage(p) {
      this.page = p;
      this.getList();
    },
    viewDetail(p) {
      this.detail = p;
      this.detailVisible = true;
    },
    async getList() {
      this.loading = true;
      try {
        const data = await this.$aesEncrypt({
          page: this.page,
          pageSize: this.pageSize,
          status: this.status,
          searchKey: this.searchKey,
        });
        const res = await this.$axios({
          url: "prompt/admin/list",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          this.list = res.data.list || [];
          this.total = res.data.total || 0;
        }
      } catch (e) {
        console.error("获取列表失败:", e);
      } finally {
        this.loading = false;
      }
    },
    review(p, act) {
      if (act === 1) {
        ElMessageBox.confirm(
          `确定通过「${p.title}」吗？通过后将在小程序端展示`,
          "通过分享",
          { confirmButtonText: "确定通过", cancelButtonText: "取消", type: "success" }
        )
          .then(() => this.doReview(p, 1, ""))
          .catch(() => {});
      } else {
        ElMessageBox.prompt(`驳回「${p.title}」，理由将反馈给作者`, "驳回分享", {
          confirmButtonText: "确定驳回",
          cancelButtonText: "取消",
          inputPlaceholder: "请填写驳回理由",
          inputPattern: /\S/,
          inputErrorMessage: "理由不能为空",
          type: "warning",
        })
          .then(({ value }) => this.doReview(p, 2, value))
          .catch(() => {});
      }
    },
    async doReview(p, act, examInfo) {
      try {
        const data = await this.$aesEncrypt({
          id: p.id,
          reviewStatus: act,
          examInfo: examInfo || "",
        });
        const res = await this.$axios({
          url: "prompt/admin/review",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          ElMessage.success(res.data?.msg || res.msg || "操作成功");
          this.getList();
        } else {
          ElMessage.error(res.msg || "操作失败");
        }
      } catch (e) {
        ElMessage.error("操作失败，请检查网络");
      }
    },
  },
};
</script>
